import type { MemoryQSyncOperation } from "@/store/note/types";

export const CACHE_KEY_DIVIDER = "|---||---|";

export type QOperation = {
  operation: MemoryQSyncOperation;
  uniqueId?: string;
};

export type StorageAction =
  | { type: "remove"; key: string; timing: "immediate" | "after-persist" }
  | { type: "set"; key: string; value: string; timing: "immediate" | "after-persist" };

export interface RestorePlan {
  operationsToRestore: QOperation[];
  storageActions: StorageAction[];
  needsPersist: boolean;
  deserializeErrors: { key: string; serializedEntry: string; error: Error }[];
}

export function computeRestorePlan({
  entries,
  currentTabKey,
  inMemoryOperationIds,
  processedOperationIds,
  deserialize,
  shouldPersistOperation,
}: {
  entries: { key: string; rawValue: string }[];
  currentTabKey: string;
  inMemoryOperationIds: Set<string>;
  processedOperationIds: Set<string>;
  deserialize: (serializedEntry: string) => QOperation;
  shouldPersistOperation: (operation: MemoryQSyncOperation) => boolean;
}): RestorePlan {
  const restoredOperations: QOperation[] = [];
  const storageActions: StorageAction[] = [];
  const deserializeErrors: RestorePlan["deserializeErrors"] = [];
  const seenOperationIds = new Set(inMemoryOperationIds);

  for (const { key, rawValue } of entries) {
    const isCurrentTabKey = key === currentTabKey;
    let restoredFromKey = false;
    let keyHasUnprocessedOperations = false;
    let keyHasUnpersistableUnprocessedOperations = false;
    let keyHadDeserializeFailure = false;

    const serializedUnknownEntries: string[] = [];
    const ops = rawValue.split(CACHE_KEY_DIVIDER).flatMap(serializedEntry => {
      try {
        return [deserialize(serializedEntry)];
      } catch (unknownError) {
        keyHadDeserializeFailure = true;
        serializedUnknownEntries.push(serializedEntry);
        deserializeErrors.push({
          key,
          serializedEntry,
          error: unknownError as Error,
        });
        return [];
      }
    });

    for (const op of ops) {
      const operationId = op.operation.id;
      const isProcessed = processedOperationIds.has(operationId);
      if (!isProcessed) {
        keyHasUnprocessedOperations = true;
        if (!shouldPersistOperation(op.operation)) {
          keyHasUnpersistableUnprocessedOperations = true;
        }
      }

      if (seenOperationIds.has(operationId)) continue;
      if (isProcessed) continue;

      seenOperationIds.add(operationId);
      restoredOperations.push(op);
      restoredFromKey = true;
    }

    // Keep the current tab snapshot unless it was consumed, so retry windows still have backup.
    const shouldConsiderRemovingKey = !isCurrentTabKey || restoredFromKey;
    if (!shouldConsiderRemovingKey) continue;

    // Preserve keys that contain entries we cannot deserialize in this runtime.
    // This avoids dropping potentially valid pending ops from newer/other clients.
    // We trim decodable entries after persistence to avoid replaying already-restored ops.
    if (keyHadDeserializeFailure) {
      if (keyHasUnpersistableUnprocessedOperations) continue;

      const rewriteValue = serializedUnknownEntries.join(CACHE_KEY_DIVIDER);
      if (!keyHasUnprocessedOperations) {
        storageActions.push(
          rewriteValue
            ? { type: "set", key, value: rewriteValue, timing: "immediate" }
            : { type: "remove", key, timing: "immediate" }
        );
        continue;
      }

      storageActions.push(
        rewriteValue
          ? { type: "set", key, value: rewriteValue, timing: "after-persist" }
          : { type: "remove", key, timing: "after-persist" }
      );
      continue;
    }

    // For operations this runtime cannot persist, we must retain the source key
    // until those operations have been executed and marked processed.
    if (keyHasUnpersistableUnprocessedOperations) continue;

    // Keys with only already-processed operations can be removed immediately.
    // Keys with unprocessed operations are removed only after successful persistence.
    if (!keyHasUnprocessedOperations) {
      storageActions.push({ type: "remove", key, timing: "immediate" });
      continue;
    }

    storageActions.push({ type: "remove", key, timing: "after-persist" });
  }

  const needsPersist =
    restoredOperations.length > 0 || storageActions.some(a => a.timing === "after-persist");

  return {
    operationsToRestore: restoredOperations,
    storageActions,
    needsPersist,
    deserializeErrors,
  };
}
